// 투자 종합현황 (2차 재구성)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, ReferenceLine, Legend, Label,
} from 'recharts';
import { fetchAccounts } from '../../api/accounts';
import {
  fetchPortfolioSummary,
  fetchPeriodChart,
  fetchPortfolioHoldings,
  fetchHoldingsDetail,
  fetchPeriodDetail,
  fetchPeriodEval,
} from '../../api/portfolio';
import axiosInstance from '../../api/axiosInstance';
import PageLayout from '../../components/PageLayout';
import SidePanel from '../../components/common/SidePanel';
import './PortfolioPage.css';

// ── 상수 ──────────────────────────────────────────────────────
const NOW_YEAR = new Date().getFullYear();
const NOW_YM   = `${NOW_YEAR}${String(new Date().getMonth() + 1).padStart(2, '0')}`;

// ── 포맷 헬퍼 ─────────────────────────────────────────────────
const fNum  = v => (v == null ? '-' : Math.round(Number(v)).toLocaleString('ko-KR'));
const fSign  = v => (Number(v) > 0 ? '+' : '');
const round2 = v => Math.round(v * 100) / 100;
const colorCls = v => (Number(v) > 0 ? 'pos' : Number(v) < 0 ? 'neg' : 'neu');

const fCard = v => {
  if (v == null) return '-';
  const sign = v < 0 ? '-' : '';
  const n = Math.abs(v);
  if (n >= 1e8) return `${sign}${(n / 1e8).toFixed(1)}억`;
  if (n >= 1e4) return `${sign}${Math.round(n / 1e4)}만`;
  return `${sign}${Math.round(n).toLocaleString('ko-KR')}`;
};

const fYAxis = v => {
  const n = Math.abs(v);
  if (n >= 1e8) return `${(v / 1e8).toFixed(0)}억`;
  if (n >= 1e4) return `${(v / 1e4).toFixed(0)}만`;
  return String(Math.round(v));
};

const formatNavYm = ym => {
  if (!ym || ym.length < 6) return '';
  return `${parseInt(ym.slice(0, 4))}년 ${parseInt(ym.slice(4))}월`;
};

const prevYm = ym => {
  const y = parseInt(ym.slice(0, 4));
  const m = parseInt(ym.slice(4));
  if (m === 1) return `${y - 1}12`;
  return `${y}${String(m - 1).padStart(2, '0')}`;
};

const nextYm = ym => {
  const y = parseInt(ym.slice(0, 4));
  const m = parseInt(ym.slice(4));
  if (m === 12) return `${y + 1}01`;
  return `${y}${String(m + 1).padStart(2, '0')}`;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const formatDateDisplay = dateStr => {
  const d = new Date(dateStr);  // UTC midnight 파싱
  const dow = WEEKDAYS[d.getUTCDay()];
  const [year, month, day] = dateStr.split('-');
  return `${year}년 ${month}월 ${day}일 (${dow})`;
};


// ── 커스텀 차트 툴팁 ───────────────────────────────────────
const makeChartTooltip = (chartType) => ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const get = key => {
    const e = payload.find(p => p.dataKey === key);
    return e != null ? Number(e.value ?? 0) : null;
  };
  const sell  = get('sell_profit') ?? 0;
  const div   = get('dividend')    ?? 0;
  const evalP = get('eval_profit');
  const fmt   = v => `${fSign(v)}${fNum(v)}원`;
  const clr   = v => (v ?? 0) >= 0 ? '#e53935' : '#1565c0';

  let g1Lbl = null, g2Lbl = null;
  if (chartType === 'daily') {
    g1Lbl = `[${label} 당일]`;
    g2Lbl = '[기간평가수익]';
  } else if (chartType === 'monthly') {
    const mo = parseInt(label.replace('월', ''), 10);
    g1Lbl = `[${label} 한달]`;
    g2Lbl = isNaN(mo) ? '[기간 누적]' : `[1월~${mo}월 누적]`;
  }

  const wrapSt = { background: '#fff', border: '1px solid #e0e4ea', borderRadius: 7,
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)', padding: '10px 14px', minWidth: 220, fontSize: 13 };
  const rowSt  = { display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 };
  const lblSt  = { color: '#555' };
  const hdrSt  = { color: '#999', fontSize: 11, marginBottom: 4, fontWeight: 500 };

  return (
    <div style={wrapSt}>
      <div style={{ fontWeight: 700, marginBottom: 8, color: '#1a1a2e' }}>{label}</div>
      {chartType === 'stock' ? (
        <>
          {evalP != null && (
            <div style={rowSt}>
              <span style={lblSt}>당일평가수익</span>
              <span style={{ color: clr(evalP) }}>{fmt(evalP)}</span>
            </div>
          )}
          <div style={rowSt}>
            <span style={lblSt}>매도확정손익</span>
            <span style={{ color: clr(sell) }}>{fmt(sell)}</span>
          </div>
        </>
      ) : (
        <>
          {g1Lbl && <div style={hdrSt}>{g1Lbl}</div>}
          <div style={rowSt}>
            <span style={lblSt}>매도확정손익</span>
            <span style={{ color: clr(sell) }}>{fmt(sell)}</span>
          </div>
          <div style={rowSt}>
            <span style={lblSt}>분배금/이자</span>
            <span style={{ color: '#f5a623' }}>{fmt(div)}</span>
          </div>
          {evalP != null && (
            <>
              <div style={{ borderTop: '1px solid #eee', margin: '7px 0 5px' }} />
              {g2Lbl && <div style={hdrSt}>{g2Lbl}</div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <span style={{ ...lblSt, fontWeight: 600 }}>평가손익</span>
                <span style={{ color: clr(evalP), fontWeight: 700 }}>{fmt(evalP)}</span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// 일별집계 진단 모달
// ════════════════════════════════════════════════════════════

const DiagnoseModal = ({ data, onClose }) => (
  <div className="pf-popup-overlay" onClick={onClose}>
    <div className="pf-popup" style={{ maxWidth: 640, width: '92vw' }} onClick={e => e.stopPropagation()}>
      <div className="pf-popup-hd">
        <span className="pf-popup-title">일별집계 진단 결과</span>
        <button className="pf-popup-close" onClick={onClose}>✕</button>
      </div>
      <div className="pf-popup-bd">
        <div style={{
          background:    '#f5f5f5',
          padding:       '12px',
          borderRadius:  '4px',
          fontSize:      '12px',
          fontFamily:    'monospace',
          whiteSpace:    'pre-wrap',
          maxHeight:     '500px',
          overflowY:     'auto',
          lineHeight:    1.5,
        }}>
          {JSON.stringify(data, null, 2)}
        </div>
      </div>
      <div className="pf-popup-ft">
        <button className="pf-btn" onClick={onClose}>닫기</button>
      </div>
    </div>
  </div>
);


// ════════════════════════════════════════════════════════════
// 재집계 팝업
// ════════════════════════════════════════════════════════════

const ReclosePopup = ({ minBuyYear, onClose, onDone }) => {
  const curYear  = new Date().getFullYear();
  const curMonth = new Date().getMonth() + 1;
  const minYear  = minBuyYear || curYear;
  const years    = Array.from({ length: curYear - minYear + 1 }, (_, i) => minYear + i);
  const months   = Array.from({ length: 12 }, (_, i) => i + 1);

  const [selYear,  setSelYear]  = useState(curYear);
  const [selMonth, setSelMonth] = useState(Math.max(1, curMonth - 1));
  const [loading,  setLoading]  = useState(false);
  const [msg,      setMsg]      = useState('');

  const handleExecute = async () => {
    const startYm = `${selYear}${String(selMonth).padStart(2, '0')}`;
    setLoading(true);
    setMsg('');
    try {
      const res = await axiosInstance.post('/monthly-close/reclose', { start_ym: startYm }, { timeout: 120000 });
      if (res.data.success) {
        setMsg(`✔ ${res.data.message}`);
        setTimeout(() => onDone(), 1200);
      } else {
        setMsg(`오류: ${res.data.message}`);
      }
    } catch (e) {
      console.error(e);
      if (e.code === 'ECONNABORTED') {
        setMsg('재집계가 예상보다 오래 걸리고 있습니다. 잠시 후 화면을 새로고침해서 결과를 확인해 주세요.');
      } else {
        setMsg('재집계 중 오류가 발생했습니다. 백엔드 터미널을 확인해 주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pf-popup-overlay" onClick={onClose}>
      <div className="pf-popup" onClick={e => e.stopPropagation()}>
        <div className="pf-popup-hd">
          <span className="pf-popup-title">집계 데이터 재계산</span>
          <button className="pf-popup-close" onClick={onClose}>✕</button>
        </div>
        <div className="pf-popup-bd">
          <div className="pf-popup-info">
            <div className="pf-popup-info-row">ℹ 집계 데이터 안내</div>
            <ul className="pf-popup-ul">
              <li>집계는 매월 말일 시세수집 완료 후 자동으로 실행됩니다.</li>
              <li>매수/매도/분배금 데이터 등록 또는 수정 시 다음 시세수집 때 자동으로 재집계됩니다.
                재집계 시 일별 및 월별 데이터를 원시데이터 기준으로 함께 재계산합니다.</li>
              <li>⚠ 이미 집계된 이후 시점의 과거 데이터를 추가/수정/삭제한 경우에는
                아래에서 시작월을 직접 선택하여 재집계를 실행해 주세요.</li>
            </ul>
          </div>
          <div className="pf-popup-field-row">
            <label className="pf-popup-label">재집계 시작월:</label>
            <select className="pf-popup-sel" value={selYear}
                    onChange={e => setSelYear(Number(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span className="pf-popup-unit">년</span>
            <select className="pf-popup-sel" value={selMonth}
                    onChange={e => setSelMonth(Number(e.target.value))}>
              {months.map(m => (
                <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
            <span className="pf-popup-unit">월</span>
          </div>
          <div className="pf-popup-note">※ 선택한 월부터 현재월까지 순차적으로 재집계합니다.</div>
          {msg && (
            <div className={`pf-popup-msg ${msg.startsWith('✔') ? 'ok' : 'err'}`}>{msg}</div>
          )}
        </div>
        <div className="pf-popup-ft">
          <button className="pf-btn primary" onClick={handleExecute} disabled={loading}>
            {loading ? '실행 중...' : '재집계 실행'}
          </button>
          <button className="pf-btn" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
};


// ════════════════════════════════════════════════════════════
// PortfolioPage
// ════════════════════════════════════════════════════════════

export default function PortfolioPage() {
  // ── 계좌 ──────────────────────────────────────────────────
  const [allAccounts,   setAllAccounts]   = useState([]);
  const [selectedAccts, setSelectedAccts] = useState([]);

  // ── 기간 ──────────────────────────────────────────────────
  const [period,       setPeriod]       = useState('today');
  const [navYm,        setNavYm]        = useState(NOW_YM);
  const [navYear,      setNavYear]      = useState(NOW_YEAR);
  const [customStart,  setCustomStart]  = useState('');
  const [customEnd,    setCustomEnd]    = useState('');
  const [customQueried,setCustomQueried]= useState(false);
  const [selectedDate, setSelectedDate] = useState(todayStr);

  // ── 재집계 팝업 ───────────────────────────────────────────
  const [showReclose,    setShowReclose]    = useState(false);
  const [minBuyYear,     setMinBuyYear]     = useState(null);
  const [reloadToken,    setReloadToken]    = useState(0);

  // ── 진단 모달 ─────────────────────────────────────────────
  const [showDiagnoseModal, setShowDiagnoseModal] = useState(false);
  const [diagnoseResult,    setDiagnoseResult]    = useState(null);
  const [diagnosing,        setDiagnosing]        = useState(false);

  // ── 자동 재집계 배너 ──────────────────────────────────────
  const [isReclosing,   setIsReclosing]   = useState(false);
  const [recloseMessage,setRecloseMessage]= useState(null);

  // ── 메인 데이터 ───────────────────────────────────────────
  const [summary,     setSummary]     = useState(null);
  const [periodChart, setPeriodChart] = useState(null);
  const [holdings,    setHoldings]    = useState(null);
  const [loading,     setLoading]     = useState(true);

  // ── 종목 상세 사이드패널 ──────────────────────────────────
  const [hPanelOpen,    setHPanelOpen]    = useState(false);
  const [holdingsDetail,setHoldingsDetail]= useState(null);
  const [hPanelLoading, setHPanelLoading] = useState(false);
  const [refreshing,    setRefreshing]    = useState(false);

  // ── 기간손익 상세 사이드패널 ─────────────────────────────
  const [pPanelOpen,    setPPanelOpen]    = useState(false);
  const [periodDetail,  setPeriodDetail]  = useState(null);
  const [pPanelLoading, setPPanelLoading] = useState(false);
  const [periodTab,     setPeriodTab]     = useState('eval');
  const [periodEval,    setPeriodEval]    = useState(null);
  const [peLoading,     setPeLoading]     = useState(false);

  // ── 드래그 분할 (② 섹션 차트) ─────────────────────────────
  const [splitPct,  setSplitPct]  = useState(60);
  const dragging    = useRef(false);
  const splitRef    = useRef(null);

  // ── 계산값 ────────────────────────────────────────────────
  const acctParam = useMemo(
    () => selectedAccts.join(','),
    [selectedAccts]
  );

  // periodParams: 항상 non-null (custom 미조회 시 fallback = today)
  const periodParams = useMemo(() => {
    if (period === 'today') return { period: 'today', date: selectedDate };
    if (period === 'month') return { period: 'month', ym: navYm };
    if (period === 'year') {
      if (navYear === NOW_YEAR) return { period: 'year' };
      return { period: 'custom', start_ym: `${navYear}01`, end_ym: `${navYear}12` };
    }
    // custom
    if (customQueried && customStart && customEnd) {
      return { period: 'custom', start_ym: customStart, end_ym: customEnd };
    }
    return { period: 'today', date: selectedDate };
  }, [period, navYm, navYear, customStart, customEnd, customQueried, selectedDate]);

  const showPeriodSection = period !== 'custom' || customQueried;

  // custom 콤보 옵션 (minBuyYear ~ NOW_YEAR)
  const ymOptions = useMemo(() => {
    const opts = [];
    const minY = minBuyYear || NOW_YEAR;
    for (let y = NOW_YEAR; y >= minY; y--) {
      const maxM = y === NOW_YEAR ? new Date().getMonth() + 1 : 12;
      for (let m = maxM; m >= 1; m--) {
        const v = `${y}${String(m).padStart(2, '0')}`;
        opts.push({ value: v, label: `${y}년 ${m}월` });
      }
    }
    return opts;
  }, [minBuyYear]);

  // ── 차트 데이터 ───────────────────────────────────────────
  const chartType = periodChart?.chart_type || 'monthly';

  // 모든 chart_type에 동일한 키 사용 (eval_profit, sell_profit, dividend)
  // monthly: eval_profit은 null 보존 (connectNulls=false 처리용)
  const chartData = useMemo(() => {
    if (!periodChart?.labels?.length) return [];
    return periodChart.labels.map((label, i) => ({
      label,
      eval_profit:  periodChart.eval_profit?.[i]  ?? null,
      sell_profit:  periodChart.sell_profit?.[i]  ?? 0,
      dividend:     periodChart.dividend?.[i]     ?? 0,
    }));
  }, [periodChart]); // eslint-disable-line

  const hasEvalLine = chartType === 'monthly' && chartData.some(d => d.eval_profit !== null);

  // 도넛 데이터 (3항목: 기간평가수익, 확정손익, 분배금)
  const donutTotal = summary
    ? (summary.period_profit ?? 0) + (summary.confirmed_profit ?? 0) + (summary.dividend ?? 0)
    : 0;

  // 매매손익 = 기간 평가수익 + 매도 확정손익 (분배금/이자 제외) - 기존 카드 값 재사용
  const tradeProfit = (summary?.period_profit ?? 0) + (summary?.confirmed_profit ?? 0);

  const donutData = useMemo(() => {
    if (!summary) return [];
    const ep = summary.period_profit ?? 0;
    const cp = summary.confirmed_profit ?? 0;
    const dv = summary.dividend ?? 0;
    return [
      { name: '기간평가수익', value: ep, absVal: Math.abs(ep),
        fill: ep >= 0 ? '#50c878' : '#aadbc2' },
      { name: '매도확정손익', value: cp, absVal: Math.abs(cp),
        fill: cp >= 0 ? '#4a90e2' : '#a8c8f0' },
      { name: '분배금/이자',  value: dv, absVal: Math.abs(dv),
        fill: '#f5a623' },
    ].filter(d => d.absVal > 0);
  }, [summary]);

  // ── 화면 진입 시 자동 재집계 확인 ───────────────────────
  useEffect(() => {
    const checkAndReclose = async () => {
      setIsReclosing(true);
      setRecloseMessage('데이터 최신화 중...');
      try {
        const res  = await axiosInstance.get('/portfolio/check-reclose');
        const data = res.data;
        if (data.reclosed) {
          setRecloseMessage(`${data.start_ym}부터 재집계 완료`);
          setReloadToken(t => t + 1);
          setTimeout(() => setRecloseMessage(null), 2000);
        } else {
          setRecloseMessage(null);
        }
      } catch (e) {
        console.error('재집계 확인 오류:', e);
        setRecloseMessage(null);
      } finally {
        setIsReclosing(false);
      }
    };
    checkAndReclose();
  }, []); // eslint-disable-line

  // ── 계좌 로드 ─────────────────────────────────────────────
  useEffect(() => {
    fetchAccounts()
      .then(data => setAllAccounts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // ── 메인 데이터 로드 ──────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, rc, rh] = await Promise.allSettled([
        fetchPortfolioSummary(acctParam, periodParams),
        fetchPeriodChart(acctParam, periodParams),
        fetchPortfolioHoldings(acctParam),
      ]);
      if (rs.status === 'fulfilled' && rs.value) {
        setSummary(rs.value);
        if (rs.value.min_buy_year) setMinBuyYear(rs.value.min_buy_year);
      }
      if (rc.status === 'fulfilled') {
        const chartResp = rc.value;
        console.log('[차트] API 응답 chart_type:', chartResp?.chart_type, '건수:', chartResp?.labels?.length, 'period:', periodParams);
        setPeriodChart(chartResp);
      }
      if (rh.status === 'fulfilled') setHoldings(rh.value);
    } finally {
      setLoading(false);
    }
  }, [acctParam, periodParams, reloadToken]); // eslint-disable-line

  useEffect(() => { loadAll(); }, [loadAll]);

  // 계좌/기간 변경 시 사이드패널 데이터 초기화
  useEffect(() => {
    setHoldingsDetail(null);
  }, [acctParam]);

  // 기간손익 패널 자동 조회 (열기/날짜/기간/계좌 변경 시)
  // deps: periodParams 객체 대신 개별 state 값을 명시 → useMemo 객체동일성 이슈 원천 차단
  // ignore 플래그: 연속 날짜 클릭 시 race condition 방지 (구식 응답 무시)
  useEffect(() => {
    if (!pPanelOpen) return;
    let ignore = false;
    console.log('[패널] period-detail 조회:', period,
      period === 'today' ? selectedDate : period === 'month' ? navYm : '');
    setPPanelLoading(true);
    setPeriodDetail(null);
    fetchPeriodDetail(acctParam, periodParams)
      .then(data => {
        if (ignore) return;
        console.log('[패널] period-detail 응답:',
          data.debug_date ?? data.period_label,
          '| 매도:', data.sell_list?.length, '분배금:', data.dividend_list?.length);
        setPeriodDetail(data);
      })
      .catch(e => { if (!ignore) console.error('[패널] 기간손익상세 오류:', e); })
      .finally(() => { if (!ignore) setPPanelLoading(false); });
    return () => { ignore = true; };
  }, [pPanelOpen, acctParam, period, selectedDate, navYm, navYear, customQueried, customStart, customEnd]); // eslint-disable-line

  // 기간평가손익 탭 자동 조회 (탭 활성화/날짜/기간/계좌 변경 시)
  useEffect(() => {
    if (!pPanelOpen || periodTab !== 'eval') return;
    let ignore = false;
    setPeLoading(true);
    setPeriodEval(null);
    fetchPeriodEval(acctParam, periodParams)
      .then(data => {
        if (ignore) return;
        console.log('[패널] period-eval 응답:',
          data.debug_date ?? '(날짜없음)',
          '| 건수:', data.items?.length);
        setPeriodEval(data);
      })
      .catch(e => { if (!ignore) console.error('[패널] 기간평가손익 오류:', e); })
      .finally(() => { if (!ignore) setPeLoading(false); });
    return () => { ignore = true; };
  }, [pPanelOpen, periodTab, acctParam, period, selectedDate, navYm, navYear, customQueried, customStart, customEnd]); // eslint-disable-line

  // ── 핸들러: 계좌 ──────────────────────────────────────────
  const toggleAcct = acct => {
    setSelectedAccts(prev =>
      prev.includes(acct) ? prev.filter(a => a !== acct) : [...prev, acct]
    );
  };

  const selectAllAccts = () => setSelectedAccts([]);

  // ── 핸들러: 진단 ──────────────────────────────────────────
  const handleDiagnose = async () => {
    setDiagnosing(true);
    setDiagnoseResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/portfolio/diagnose-daily', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rawText = await res.text();
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (e) {
        data = { raw_error: rawText };
      }
      setDiagnoseResult(data);
    } catch (e) {
      setDiagnoseResult({ raw_error: e.message });
    } finally {
      setDiagnosing(false);
      setShowDiagnoseModal(true);
    }
  };

  // ── 핸들러: 기간 ──────────────────────────────────────────
  const handlePeriodChange = p => {
    setPeriod(p);
    if (p !== 'custom') setCustomQueried(false);
    if (p === 'today') setSelectedDate(todayStr());
    if (p === 'month') {
      const now = new Date();
      setNavYm(`${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`);
    }
  };

  // ── 핸들러: 오늘 날짜 이동 (UTC 기준 — 로컬타임 오프셋 버그 방지) ──
  const handleDayMove = direction => {
    const cur = new Date(selectedDate);       // UTC midnight으로 파싱
    cur.setUTCDate(cur.getUTCDate() + direction);
    const newDate = cur.toISOString().slice(0, 10);
    if (newDate > todayStr()) return;
    setSelectedDate(newDate);
  };

  // ── 핸들러: 사이드패널 (종목 상세) ───────────────────────
  const openHoldingsPanel = async () => {
    setHPanelOpen(true);
    if (!holdingsDetail) {
      setHPanelLoading(true);
      try {
        const data = await fetchHoldingsDetail(acctParam);
        setHoldingsDetail(data);
      } catch (e) {
        console.error(e);
      } finally {
        setHPanelLoading(false);
      }
    }
  };

  // ── 핸들러: 실시간 시세 새로고침 ─────────────────────────
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const tickerSet = new Set((holdings || []).map(h => h.ticker).filter(Boolean));
      if (tickerSet.size > 0) {
        const res = await axiosInstance.get('/price/realtime', {
          params: { tickers: [...tickerSet].join(',') },
        });
        const priceMap = {};
        (res.data?.data || []).forEach(p => { priceMap[p.ticker] = p; });

        // holdings 상태 갱신 (평가금액·손익·오늘등락 업데이트)
        const newHoldings = (holdings || []).map(h => {
          const p = priceMap[h.ticker];
          if (!p?.current_price) return h;
          const newEval   = p.current_price * (h.total_quantity || 0);
          const newProfit = newEval - (h.buy_amount || 0);
          const newRate   = h.buy_amount > 0 ? newProfit / h.buy_amount * 100 : 0;
          return {
            ...h,
            eval_amount:      round2(newEval),
            eval_profit:      round2(newProfit),
            eval_profit_rate: round2(newRate),
            today_diff:       p.prev_diff  ?? null,
            today_diff_rate:  p.diff_rate  ?? null,
          };
        });
        setHoldings(newHoldings);

        // 실시간 포트폴리오 현황 카드 재계산
        const totalBuy   = newHoldings.reduce((s, h) => s + (h.buy_amount  || 0), 0);
        const totalEval  = newHoldings.reduce((s, h) => s + (h.eval_amount || 0), 0);
        const evalProfit = totalEval - totalBuy;
        const todayProfit = newHoldings.reduce((s, h) =>
          s + (h.today_diff != null ? h.today_diff * (h.total_quantity || 0) : 0), 0);
        setSummary(prev => prev ? {
          ...prev,
          total_eval:       totalEval,
          eval_profit:      round2(evalProfit),
          eval_profit_rate: round2(totalBuy > 0 ? evalProfit / totalBuy * 100 : 0),
          today_profit:     todayProfit,
        } : prev);

        // 종목 상세 패널 갱신 (열려 있으면 클라이언트 업데이트, 닫혀 있으면 캐시 무효화)
        if (hPanelOpen && holdingsDetail) {
          setHoldingsDetail(holdingsDetail.map(h => {
            const p = priceMap[h.ticker];
            if (!p?.current_price) return h;
            const newEval   = p.current_price * (h.quantity || 0);
            const newProfit = newEval - (h.buy_amount || 0);
            const newRate   = h.buy_amount > 0 ? newProfit / h.buy_amount * 100 : 0;
            return {
              ...h,
              current_price:    p.current_price,
              eval_amount:      round2(newEval),
              eval_profit:      round2(newProfit),
              eval_profit_rate: round2(newRate),
              today_diff:       p.prev_diff  ?? null,
              today_diff_rate:  p.diff_rate  ?? null,
            };
          }));
        } else {
          setHoldingsDetail(null);
        }
      }
    } catch (e) {
      console.error('시세 갱신 오류:', e);
    } finally {
      setRefreshing(false);
    }
  };

  // ── 핸들러: 사이드패널 (기간손익 상세) ───────────────────
  const openPeriodPanel = () => {
    setPPanelOpen(true);
    // 데이터 조회는 위 useEffect에서 자동 처리
  };

  // ── 드래그 핸들러 ─────────────────────────────────────────
  const onDragStart = e => {
    dragging.current = true;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = e => {
      if (!dragging.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const pct  = Math.min(80, Math.max(30,
        ((e.clientX - rect.left) / rect.width) * 100
      ));
      setSplitPct(Math.round(pct));
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, []);

  // ── 도넛 커스텀 레이블 (바깥 라인) ─────────────────────────
  const RADIAN = Math.PI / 180;
  const renderDonutLabel = ({ cx, cy, midAngle, outerRadius, name, payload }) => {
    const r  = outerRadius + 26;
    const x  = cx + r * Math.cos(-midAngle * RADIAN);
    const y  = cy + r * Math.sin(-midAngle * RADIAN);
    const anchor = x > cx ? 'start' : 'end';
    return (
      <text x={x} y={y} textAnchor={anchor} fill="#555" fontSize={10.5}>
        {name}: {fSign(payload.value)}{fCard(payload.value)}원
      </text>
    );
  };

  // ── 기간별 그래프 렌더 (chart_type 분기) ──────────────────
  const renderPeriodChart = () => {
    if (!chartData.length) {
      return (
        <div className="pf-empty" style={{ marginTop: 16 }}>
          {chartType === 'stock'
            ? '오늘 시세수집 후 데이터가 생성됩니다.'
            : period === 'month'
            ? '일별 집계 데이터가 없습니다. 재집계 버튼을 클릭하여 데이터를 생성해 주세요.'
            : '해당 기간의 데이터가 없습니다.'}
        </div>
      );
    }

    // 오늘: 종목별 당일 평가수익
    if (chartType === 'stock') {
      return (
        <ResponsiveContainer width="100%" height={292}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 8, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" angle={-45} textAnchor="end"
                   height={50} interval={0} tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={fYAxis} tick={{ fontSize: 10 }} width={52} />
            <Tooltip content={makeChartTooltip(chartType)} />
            <ReferenceLine y={0} stroke="#bbb" strokeWidth={1} />
            <Bar dataKey="eval_profit" name="당일평가수익" maxBarSize={24}>
              {chartData.map((d, i) => (
                <Cell key={i} fill={(d.eval_profit ?? 0) >= 0 ? '#e53935' : '#1565c0'} />
              ))}
            </Bar>
            <Bar dataKey="sell_profit" name="매도확정손익" fill="#f5a623" maxBarSize={24} />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
          </ComposedChart>
        </ResponsiveContainer>
      );
    }

    // 이번달: 일별 평가수익 라인 + 매도/분배금 바
    if (chartType === 'daily') {
      return (
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 8, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" angle={-45} textAnchor="end"
                   height={42} interval={0} tick={{ fontSize: 10 }} />
            <YAxis tickFormatter={fYAxis} tick={{ fontSize: 10 }} width={52} />
            <Tooltip content={makeChartTooltip(chartType)} />
            <ReferenceLine y={0} stroke="#bbb" strokeWidth={1} />
            <Bar dataKey="sell_profit" name="매도확정손익" maxBarSize={10}>
              {chartData.map((d, i) => (
                <Cell key={i} fill={d.sell_profit >= 0 ? '#e53935' : '#1565c0'} />
              ))}
            </Bar>
            <Bar dataKey="dividend" name="분배금/이자" fill="#f5a623" maxBarSize={10} />
            <Line dataKey="eval_profit" name="기간평가수익"
                  stroke="#1a237e" strokeWidth={2}
                  dot={{ r: 3, fill: '#1a237e' }} type="monotone" />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
          </ComposedChart>
        </ResponsiveContainer>
      );
    }

    // 올해/직접설정: 월별
    return (
      <ResponsiveContainer width="100%" height={292}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 8, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={fYAxis} tick={{ fontSize: 10 }} width={52} />
          <Tooltip content={makeChartTooltip(chartType)} />
          <ReferenceLine y={0} stroke="#bbb" strokeWidth={1} />
          <Bar dataKey="sell_profit" name="매도확정손익" maxBarSize={22}>
            {chartData.map((d, i) => (
              <Cell key={i}
                    fill={d.sell_profit > 0 ? '#e53935'
                        : d.sell_profit < 0 ? '#1565c0' : '#ccc'} />
            ))}
          </Bar>
          <Bar dataKey="dividend" name="분배금/이자" fill="#f5a623" maxBarSize={22} />
          {hasEvalLine && (
            <Line dataKey="eval_profit" name="평가손익"
                  stroke="#1a237e" strokeWidth={2}
                  dot={{ r: 3, fill: '#1a237e' }}
                  connectNulls={false} type="monotone" />
          )}
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  // ── 렌더 ──────────────────────────────────────────────────
  return (
    <PageLayout title="투자 종합현황" scrollable>
      {/* ── 자동 재집계 배너 ─────────────────────────────── */}
      {recloseMessage && (
        <div className={`pf-reclose-banner${isReclosing ? ' checking' : ' done'}`}>
          {isReclosing ? '🔄' : '✅'} {recloseMessage}
        </div>
      )}

      {showReclose && (
        <ReclosePopup
          minBuyYear={minBuyYear}
          onClose={() => setShowReclose(false)}
          onDone={() => {
            setShowReclose(false);
            setReloadToken(t => t + 1);
          }}
        />
      )}

      {showDiagnoseModal && (
        <DiagnoseModal
          data={diagnoseResult}
          onClose={() => setShowDiagnoseModal(false)}
        />
      )}

      {/* ── 툴바: 계좌 + 재집계 ─────────────────────────── */}
      <div className="pf-toolbar">
        <div className="pf-acct-btns">
          <button
            className={`pf-acct-btn${selectedAccts.length === 0 ? ' active' : ''}`}
            onClick={selectAllAccts}
          >
            전체
          </button>
          {allAccounts.map(a => (
            <button
              key={a.account_name}
              className={`pf-acct-btn${selectedAccts.includes(a.account_name) ? ' active' : ''}`}
              onClick={() => toggleAcct(a.account_name)}
            >
              {a.account_name}
            </button>
          ))}
        </div>
        <button
          className={`pf-refresh-btn${refreshing ? ' spinning' : ''}`}
          onClick={handleRefresh}
          disabled={refreshing}
          title="실시간 시세 반영하여 재조회"
        >
          ↻
        </button>
        <button className="pf-diagnose-btn" onClick={handleDiagnose}
                disabled={diagnosing} title="일별집계 진단">
          {diagnosing ? '진단 중...' : '🔍 진단'}
        </button>
        <button className="pf-reclose-btn" onClick={() => setShowReclose(true)}
                title="집계 데이터 재계산">
          재집계 ↻
        </button>
      </div>

      {/* ════════════════════════════════════════════════ */}
      {/* ① 실시간 포트폴리오 현황                       */}
      {/* ════════════════════════════════════════════════ */}
      <div className="pf-section">
        <div className="pf-section-header" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="pf-section-num">①</span>
            <span className="pf-section-title">실시간 포트폴리오 현황</span>
          </div>
          <button className="pf-detail-btn" onClick={openHoldingsPanel}>
            📋 종목별 상세 보기 ▶
          </button>
        </div>

        {/* 4카드 */}
        <div className="pf-cards-4">
          <SummaryCard
            label="총 투자금"
            value={summary?.total_buy}
            sub="매수금액 합계"
            tip={"현재 보유 중인 종목의\n매수금액 합계입니다.\n선택한 계좌 기준입니다."}
          />
          <SummaryCard
            label="현재 평가금"
            value={summary?.total_eval}
            sub="실시간 시세 기준"
            tip={"오늘 수집된 최신 시세 기준으로\n보유수량 × 현재가로 계산한\n평가금액 합계입니다."}
          />
          <SummaryCard
            label="평가 손익"
            value={summary?.eval_profit}
            signed
            sub={summary ? `${fSign(summary.eval_profit_rate)}${Number(summary.eval_profit_rate).toFixed(2)}%` : ''}
            tip={"현재 평가금액 - 총 투자금\n양수(+): 평가이익 (빨강)\n음수(-): 평가손실 (파랑)\n실현되지 않은 미확정 손익입니다."}
          />
          <SummaryCard
            label="수익률"
            value={summary?.eval_profit_rate}
            unit="%"
            signed
            decimals={2}
            sub={summary?.today_profit != null
              ? `오늘 ${fSign(summary.today_profit)}${fCard(summary.today_profit)}원`
              : ''}
            tip={"평가손익 ÷ 총 투자금 × 100\n실시간 시세 기준 수익률입니다."}
          />
        </div>

      </div>

      {/* ── 기간 선택 바 ───────────────────────────────── */}
      <div className="pf-period-bar">
        <div className="pf-period-btns">
          {[
            { v: 'today', label: '오늘' },
            { v: 'month', label: '이번달' },
            { v: 'year',  label: '올해' },
            { v: 'custom',label: '직접설정' },
          ].map(({ v, label }) => (
            <button
              key={v}
              className={`pf-p-btn${period === v ? ' active' : ''}`}
              onClick={() => handlePeriodChange(v)}
            >
              {label}
            </button>
          ))}
        </div>

        {period === 'today' && (
          <div className="pf-nav-row">
            <button className="pf-nav-btn" onClick={() => handleDayMove(-1)}>◀</button>
            <span className="pf-nav-label" style={{ minWidth: 150, textAlign: 'center' }}>
              {formatDateDisplay(selectedDate)}
            </span>
            <button className="pf-nav-btn" onClick={() => handleDayMove(1)}
                    disabled={selectedDate >= todayStr()}>▶</button>
          </div>
        )}

        {period === 'month' && (
          <div className="pf-nav-row">
            <button className="pf-nav-btn" onClick={() => setNavYm(prevYm(navYm))}>◀</button>
            <span className="pf-nav-label">{formatNavYm(navYm)}</span>
            <button className="pf-nav-btn" onClick={() => setNavYm(nextYm(navYm))}
                    disabled={navYm >= NOW_YM}>▶</button>
          </div>
        )}

        {period === 'year' && (
          <div className="pf-nav-row">
            <button className="pf-nav-btn" onClick={() => setNavYear(y => y - 1)}>◀</button>
            <span className="pf-nav-label">{navYear}년</span>
            <button className="pf-nav-btn" onClick={() => setNavYear(y => y + 1)}
                    disabled={navYear >= NOW_YEAR}>▶</button>
          </div>
        )}

        {period === 'custom' && (
          <div className="pf-custom-row">
            <select
              className="pf-custom-sel"
              value={customStart}
              onChange={e => { setCustomStart(e.target.value); setCustomQueried(false); }}
            >
              <option value="">시작월</option>
              {ymOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span className="pf-custom-tilde">~</span>
            <select
              className="pf-custom-sel"
              value={customEnd}
              onChange={e => { setCustomEnd(e.target.value); setCustomQueried(false); }}
            >
              <option value="">종료월</option>
              {ymOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button
              className="pf-custom-query-btn"
              onClick={() => setCustomQueried(true)}
              disabled={!customStart || !customEnd}
            >
              조회
            </button>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════ */}
      {/* ② 기간별 손익 현황                             */}
      {/* ════════════════════════════════════════════════ */}
      <div className="pf-section pf-period-section">
        <div className="pf-section-header" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="pf-section-num">②</span>
            <span className="pf-section-title">기간별 손익 현황</span>
          </div>
          <button className="pf-detail-btn" onClick={openPeriodPanel}>
            📋 기간 손익 상세 보기 ▶
          </button>
        </div>

        {!showPeriodSection ? (
          <div className="pf-empty">기간을 선택하고 조회 버튼을 눌러주세요.</div>
        ) : loading ? (
          <div className="pf-loading">데이터를 불러오는 중...</div>
        ) : (
          <>
            {/* 5카드: [기간평가수익] + [매도확정손익] = [매매손익] + [분배금/이자] = [기간 총수익] */}
            <div className="pf-period-4cards-row pf-period-5cards-row">
              <PeriodCard
                label="기간 평가수익"
                value={summary?.period_profit}
                rate={summary?.period_profit_rate}
                sub={summary?.period_is_temp
                  ? '실시간 시세 기준'
                  : `${summary?.period_profit_label || ''} 집계 기준`}
                tip={"선택 기간의 보유 종목\n평가금액 변화분입니다.\n계산식:\n기말 평가금액\n- 기초 평가금액\n- 기간 중 순투자금액\n(매도된 종목의 손익은\n매도 확정손익에 반영됩니다)\n\n평가손익률:\n기간 평가수익 ÷\n(기초평가액 + 기간순매수원금) × 100"}
              />
              <span className="pf-period-op">+</span>
              <PeriodCard
                label="매도 확정손익"
                value={summary?.confirmed_profit}
                sub="실현손익"
                tip={"선택 기간 내 실제 매도로\n확정된 손익 합계입니다.\n계산식: 매도금액 - 매수원금\n이미 실현된 확정 수익/손실입니다."}
              />
              <span className="pf-period-op">=</span>
              <PeriodCard
                label="매매손익"
                value={tradeProfit}
                sub="평가 + 매도 (분배·이자 제외)"
                isSubtotal
                tip={"기간 평가수익\n+ 매도 확정손익\n(분배금/이자는 제외한\n매매 자체의 손익입니다)"}
              />
              <span className="pf-period-op">+</span>
              <PeriodCard
                label="분배금 / 이자"
                value={summary?.dividend}
                sub="수령액"
                alwaysPos
                tip={"선택 기간 내 수령한\n분배금 및 이자 합계입니다.\n실제 입금된 확정 수익입니다.\n\n※ 일별(오늘) 조회 시\n   분배금/이자는 매월 1일자에만\n   표시됩니다."}
              />
              <span className="pf-period-op">=</span>
              <PeriodCard
                label="기간 총수익"
                value={donutTotal}
                sub="매매손익 + 분배"
                isTotal
                tip={"매매손익(기간 평가수익\n+ 매도 확정손익)\n+ 분배금/이자 수령\n선택 기간의 전체 투자 성과입니다."}
              />
            </div>

            {/* 차트 영역 (드래그 분할) */}
            {chartData.length > 0 ? (
              <div className="pf-split-wrap" ref={splitRef}>
                {/* 왼쪽: 기간별 그래프 */}
                <div className="pf-split-left" style={{ width: `${splitPct}%` }}>
                  {renderPeriodChart()}
                </div>

                {/* 드래그 핸들 */}
                <div className="pf-split-divider" onMouseDown={onDragStart} />

                {/* 오른쪽: 도넛 */}
                <div className="pf-split-right" style={{ width: `${100 - splitPct}%` }}>
                  {donutData.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={208}>
                        <PieChart>
                          <Pie
                            data={donutData}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={86}
                            dataKey="absVal"
                            nameKey="name"
                            label={renderDonutLabel}
                            labelLine
                            paddingAngle={2}
                          >
                            {donutData.map((d, i) => (
                              <Cell key={i} fill={d.fill} />
                            ))}
                            <Label
                              content={({ viewBox }) => {
                                const { cx, cy } = viewBox;
                                return (
                                  <g>
                                    <text x={cx} y={cy - 9} textAnchor="middle"
                                          fill="#888" fontSize={10.5}>총 수익</text>
                                    <text x={cx} y={cy + 9} textAnchor="middle"
                                          fill={donutTotal >= 0 ? '#c62828' : '#1565c0'}
                                          fontSize={14} fontWeight={700}>
                                      {fSign(donutTotal)}{fCard(donutTotal)}원
                                    </text>
                                  </g>
                                );
                              }}
                              position="center"
                            />
                          </Pie>
                          <Tooltip
                            formatter={(v, name, props) => [
                              `${fSign(props.payload.value)}${fNum(props.payload.value)}원`,
                              name,
                            ]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      {/* 범례 */}
                      <div className="pf-donut-legend">
                        {donutData.map((d, i) => (
                          <div key={i} className="pf-donut-legend-item">
                            <span className="pf-donut-dot" style={{ background: d.fill }} />
                            <span className="pf-donut-name">{d.name}</span>
                            <span className={`pf-donut-val ${colorCls(d.value)}`}>
                              {fSign(d.value)}{fCard(d.value)}원
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="pf-empty" style={{ marginTop: 60 }}>수익 데이터 없음</div>
                  )}
                </div>
              </div>
            ) : (
              renderPeriodChart()
            )}
          </>
        )}
      </div>

      {/* ════════════════════════════════════════════════ */}
      {/* 사이드패널: 종목 상세                          */}
      {/* ════════════════════════════════════════════════ */}
      <SidePanel
        open={hPanelOpen}
        title="종목별 실시간 현황"
        onClose={() => setHPanelOpen(false)}
      >
        {hPanelLoading ? (
          <div className="sp-loading">불러오는 중...</div>
        ) : (
          <HoldingsDetailPanel data={holdingsDetail} />
        )}
      </SidePanel>

      {/* ════════════════════════════════════════════════ */}
      {/* 사이드패널: 기간 손익 상세 (탭)               */}
      {/* ════════════════════════════════════════════════ */}
      <SidePanel
        open={pPanelOpen}
        title="기간 손익 상세"
        onClose={() => setPPanelOpen(false)}
      >
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px',
                      paddingBottom: '8px', borderBottom: '1px solid #eee' }}>
          📅 {period === 'today'
            ? `${formatDateDisplay(selectedDate)}`
            : period === 'month'
            ? formatNavYm(navYm)
            : period === 'year'
            ? `${NOW_YEAR}년`
            : customStart && customEnd
            ? `${customStart.slice(0,4)}.${customStart.slice(4)} ~ ${customEnd.slice(0,4)}.${customEnd.slice(4)}`
            : ''
          } 기준
        </div>
        <div className="sp-tab-btns">
          <button className={`sp-tab-btn${periodTab === 'eval' ? ' active' : ''}`}
                  onClick={() => setPeriodTab('eval')}>
            기간 평가손익
          </button>
          <button className={`sp-tab-btn${periodTab === 'sell' ? ' active' : ''}`}
                  onClick={() => setPeriodTab('sell')}>
            매도 확정손익
          </button>
          <button className={`sp-tab-btn${periodTab === 'div' ? ' active' : ''}`}
                  onClick={() => setPeriodTab('div')}>
            분배금 / 이자
          </button>
        </div>

        {/* key 변경 시 콘텐츠 강제 재렌더링 — 이전 데이터 잔상 방지 */}
        <div key={`panel-${period}-${period === 'today' ? selectedDate : period === 'month' ? navYm : navYear}`}>
          {periodTab === 'eval' ? (
            peLoading ? <div className="sp-loading">불러오는 중...</div>
                      : <PeriodEvalTab data={periodEval?.items} />
          ) : pPanelLoading ? (
            <div className="sp-loading">불러오는 중...</div>
          ) : periodTab === 'sell' ? (
            <PeriodSellTab data={periodDetail?.sell_list} />
          ) : (
            <PeriodDivTab data={periodDetail?.dividend_list} />
          )}
        </div>
      </SidePanel>
    </PageLayout>
  );
}


// ── 종목별 상세 패널 ─────────────────────────────────────────

function HoldingsDetailPanel({ data }) {
  if (!data?.length) return <div className="sp-empty">데이터가 없습니다.</div>;

  // 계좌별 그룹화 (API 정렬 순서 유지)
  const accountOrder = [];
  const accountMap   = {};
  data.forEach(r => {
    if (!accountMap[r.account_name]) {
      accountMap[r.account_name] = [];
      accountOrder.push(r.account_name);
    }
    accountMap[r.account_name].push(r);
  });

  // 계좌별 소계 계산
  const acctStats = accountOrder.map(acct => {
    const items     = accountMap[acct];
    const subBuy    = items.reduce((s, r) => s + (r.buy_amount  || 0), 0);
    const subEval   = items.reduce((s, r) => s + (r.eval_amount || 0), 0);
    const subProfit = subEval - subBuy;
    const subTodayM = items.reduce((s, r) =>
      s + (r.today_diff != null && r.quantity != null ? r.today_diff * r.quantity : 0), 0);
    return {
      acct, items, subBuy, subEval, subProfit,
      subRate:   subBuy > 0 ? subProfit / subBuy * 100 : 0,
      subTodayM,
    };
  });

  // 전체 합계
  const grandBuy    = acctStats.reduce((s, a) => s + a.subBuy,    0);
  const grandEval   = acctStats.reduce((s, a) => s + a.subEval,   0);
  const grandToday  = acctStats.reduce((s, a) => s + a.subTodayM, 0);
  const grandProfit = grandEval - grandBuy;
  const grandRate   = grandBuy > 0 ? grandProfit / grandBuy * 100 : 0;

  const todayMoneyCell = (v, cls = '') => (
    <td className={`num ${cls || (v > 0 ? 'pos' : v < 0 ? 'neg' : 'neu')}`}>
      {v !== 0 ? `${fSign(v)}${fNum(v)}` : '-'}
    </td>
  );

  return (
    <table className="sp-tbl">
      <thead>
        <tr>
          <th>계좌</th>
          <th>종목명</th>
          <th className="num">평균단가</th>
          <th className="num">수량</th>
          <th className="num">매수금액</th>
          <th className="num">현재시세</th>
          <th className="num">오늘 등락</th>
          <th className="num">평가금액</th>
          <th className="num">평가손익</th>
          <th className="num">수익률</th>
        </tr>
      </thead>
      <tbody>
        {acctStats.map(({ acct, items, subBuy, subEval, subProfit, subRate, subTodayM }, ai) => (
          <React.Fragment key={acct}>
            {items.map((r, ri) => {
              const ep  = r.eval_profit      ?? 0;
              const er  = r.eval_profit_rate ?? 0;
              const td  = r.today_diff;
              const tdr = r.today_diff_rate;
              return (
                <tr key={`${ai}-${ri}`}>
                  <td>{r.account_name}</td>
                  <td>{r.stock_name}</td>
                  <td className="num">{fNum(r.avg_price)}</td>
                  <td className="num">
                    {Number(r.quantity).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}
                  </td>
                  <td className="num">{fNum(r.buy_amount)}</td>
                  <td className="num">{r.current_price != null ? fNum(r.current_price) : '-'}</td>
                  <td className={`num ${td == null ? 'neu' : colorCls(td)}`}>
                    {td == null ? '-'
                      : `${fSign(td)}${fNum(td)}${tdr != null
                          ? ` (${fSign(tdr)}${Math.abs(Number(tdr)).toFixed(2)}%)`
                          : ''}`}
                  </td>
                  <td className="num">{fNum(r.eval_amount)}</td>
                  <td className={`num ${colorCls(ep)}`}>{fSign(ep)}{fNum(ep)}</td>
                  <td className={`num ${colorCls(er)}`}>{fSign(er)}{Number(er).toFixed(2)}%</td>
                </tr>
              );
            })}
            {/* 계좌 소계 행 */}
            <tr className="sp-subtotal-row">
              <td></td>
              <td>{acct} 소계</td>
              <td></td><td></td>
              <td className="num">{fNum(subBuy)}</td>
              <td></td>
              {todayMoneyCell(subTodayM)}
              <td className="num">{fNum(subEval)}</td>
              <td className={`num ${colorCls(subProfit)}`}>{fSign(subProfit)}{fNum(subProfit)}</td>
              <td className={`num ${colorCls(subRate)}`}>{fSign(subRate)}{Number(subRate).toFixed(2)}%</td>
            </tr>
          </React.Fragment>
        ))}
        {/* 전체 합계 행 */}
        <tr className="sp-total-row">
          <td></td>
          <td>전체 합계</td>
          <td></td><td></td>
          <td className="num">{fNum(grandBuy)}</td>
          <td></td>
          {todayMoneyCell(grandToday)}
          <td className="num">{fNum(grandEval)}</td>
          <td className={`num ${colorCls(grandProfit)}`}>{fSign(grandProfit)}{fNum(grandProfit)}</td>
          <td className={`num ${colorCls(grandRate)}`}>{fSign(grandRate)}{Number(grandRate).toFixed(2)}%</td>
        </tr>
      </tbody>
    </table>
  );
}


// ── 물음표 툴팁 ──────────────────────────────────────────────

function InfoTip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="pf-tip-wrap">
      <span
        className="pf-tip-icon"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >?</span>
      {show && <div className="pf-tip-box">{text}</div>}
    </span>
  );
}


// ── 요약카드 (Section ①) ─────────────────────────────────────

function SummaryCard({ label, value, signed, unit, decimals, sub, tip }) {
  const v = value ?? null;
  const display = v == null ? '-'
    : decimals != null
      ? `${signed && v > 0 ? '+' : ''}${Number(v).toFixed(decimals)}${unit || ''}`
      : `${signed && v > 0 ? '+' : ''}${fCard(v)}${unit ? unit : '원'}`;
  const cls = signed && v != null ? colorCls(v) : '';
  const exact = !unit && v != null && Math.abs(v) >= 10000 ? `${fNum(v)}원` : null;
  return (
    <div className="pf-card">
      <div className="pf-card-top">
        <div className="pf-card-label">
          {label}{tip && <InfoTip text={tip} />}
        </div>
        <div className={`pf-card-value ${cls}`}>{display}</div>
      </div>
      {(sub || exact) && (
        <div className="pf-card-detail">
          {sub}{sub && exact ? ' · ' : ''}{exact}
        </div>
      )}
    </div>
  );
}


// ── 기간 카드 (Section ②) ───────────────────────────────────

function PeriodCard({ label, value, sub, alwaysPos, isTotal, isSubtotal, tip, rate }) {
  const v = value ?? null;
  const cls = alwaysPos ? 'pos' : (v != null ? colorCls(v) : '');
  const cardCls = `pf-period-card${isTotal ? ` pf-period-card-total ${cls}` : ''}${isSubtotal ? ' pf-period-card-subtotal' : ''}`;
  const exact = v != null && Math.abs(v) >= 10000 ? `${fSign(v)}${fNum(v)}원` : null;
  const rateText = rate != null ? `${fSign(rate)}${Number(rate).toFixed(2)}%` : null;
  const detailParts = [sub, rateText, exact].filter(Boolean);
  return (
    <div className={cardCls}>
      <div className="pf-period-card-top">
        <div className="pf-period-card-label">
          {label}{tip && <InfoTip text={tip} />}
        </div>
        <div className={`pf-period-card-value ${isTotal ? '' : cls}`}>
          {v == null ? '-' : `${v > 0 ? '+' : ''}${fCard(v)}원`}
        </div>
      </div>
      {detailParts.length > 0 && (
        <div className="pf-period-card-detail">
          {detailParts.join(' · ')}
        </div>
      )}
    </div>
  );
}


// ── 탭: 기간 평가손익 ───────────────────────────────────────

function PeriodEvalTab({ data }) {
  if (!data) return <div className="sp-empty">데이터가 없습니다.</div>;
  if (!data.length) return <div className="sp-empty">해당 기간의 평가손익 데이터가 없습니다.</div>;

  // 계좌별 그룹화 (API 정렬 순서 유지)
  const acctOrder = [];
  const acctMap   = {};
  data.forEach(r => {
    if (!acctMap[r.account_name]) {
      acctMap[r.account_name] = [];
      acctOrder.push(r.account_name);
    }
    acctMap[r.account_name].push(r);
  });

  const grandProfit = data.reduce((s, r) => s + (r.eval_profit ?? 0), 0);
  const grandStart  = data.reduce((s, r) => s + (r.start_amount ?? 0), 0);
  const grandEnd    = data.reduce((s, r) => s + (r.end_amount   ?? 0), 0);

  return (
    <>
      <table className="sp-tbl">
        <thead>
          <tr>
            <th>계좌</th>
            <th>종목명</th>
            <th className="num">기초수량</th>
            <th className="num">기초시세</th>
            <th className="num">기초금액</th>
            <th className="num">기말수량</th>
            <th className="num">기말시세</th>
            <th className="num">기말금액</th>
            <th className="num">평가손익</th>
          </tr>
        </thead>
        <tbody>
          {acctOrder.map(acct => {
            const items = acctMap[acct];
            const subStart  = items.reduce((s, r) => s + (r.start_amount ?? 0), 0);
            const subEnd    = items.reduce((s, r) => s + (r.end_amount   ?? 0), 0);
            const subProfit = items.reduce((s, r) => s + (r.eval_profit  ?? 0), 0);
            return (
              <React.Fragment key={acct}>
                {items.map((r, i) => {
                  const ep = r.eval_profit ?? 0;
                  return (
                    <tr key={i}>
                      <td>{r.account_name}</td>
                      <td>{r.stock_name}</td>
                      <td className="num">{r.start_quantity != null ? Number(r.start_quantity).toLocaleString('ko-KR', { maximumFractionDigits: 4 }) : '-'}</td>
                      <td className="num">{r.start_price   != null ? fNum(r.start_price)  : '-'}</td>
                      <td className="num">{r.start_amount  != null ? fNum(r.start_amount) : '-'}</td>
                      <td className="num">{r.end_quantity  != null ? Number(r.end_quantity).toLocaleString('ko-KR', { maximumFractionDigits: 4 }) : '-'}</td>
                      <td className="num">{r.end_price     != null ? fNum(r.end_price)    : '-'}</td>
                      <td className="num">{r.end_amount    != null ? fNum(r.end_amount)   : '-'}</td>
                      <td className={`num ${colorCls(ep)}`}>{ep !== 0 ? `${fSign(ep)}${fNum(ep)}` : '-'}</td>
                    </tr>
                  );
                })}
                <tr className="sp-subtotal-row">
                  <td></td>
                  <td>{acct} 소계</td>
                  <td></td><td></td>
                  <td className="num">{fNum(subStart)}</td>
                  <td></td><td></td>
                  <td className="num">{fNum(subEnd)}</td>
                  <td className={`num ${colorCls(subProfit)}`}>{fSign(subProfit)}{fNum(subProfit)}</td>
                </tr>
              </React.Fragment>
            );
          })}
          <tr className="sp-total-row">
            <td></td>
            <td>전체 합계</td>
            <td></td><td></td>
            <td className="num">{fNum(grandStart)}</td>
            <td></td><td></td>
            <td className="num">{fNum(grandEnd)}</td>
            <td className={`num ${colorCls(grandProfit)}`}>{fSign(grandProfit)}{fNum(grandProfit)}</td>
          </tr>
        </tbody>
      </table>
      <div className="pf-eval-note">
        ℹ 종목 매도 시 매도원금 만큼 기간 평가손익에서 제외됩니다.
      </div>
    </>
  );
}


// ── 탭: 매도확정손익 ────────────────────────────────────────

function PeriodSellTab({ data }) {
  if (!data) return <div className="sp-empty">데이터가 없습니다.</div>;
  if (!data.length) return <div className="sp-empty">해당 기간의 매도 내역이 없습니다.</div>;

  // 계좌별 그룹화 (API 정렬 순서 유지)
  const acctOrder = [];
  const acctMap   = {};
  data.forEach(r => {
    if (!acctMap[r.account_name]) {
      acctMap[r.account_name] = [];
      acctOrder.push(r.account_name);
    }
    acctMap[r.account_name].push(r);
  });

  const grandProfit = data.reduce((s, r) => s + (r.confirmed_profit ?? 0), 0);
  const grandBuyAmt = data.reduce((s, r) => s + (r.buy_amount       ?? 0), 0);
  const grandSellAmt= data.reduce((s, r) => s + (r.sell_amount      ?? 0), 0);

  return (
    <table className="sp-tbl">
      <thead>
        <tr>
          <th>계좌</th>
          <th>종목명</th>
          <th className="ctr">매도일</th>
          <th className="num">매수단가</th>
          <th className="num">매도단가</th>
          <th className="num">수량</th>
          <th className="num">매수금액</th>
          <th className="num">매도금액</th>
          <th className="num">확정손익</th>
          <th className="num">수익률</th>
        </tr>
      </thead>
      <tbody>
        {acctOrder.map(acct => {
          const items     = acctMap[acct];
          const subProfit = items.reduce((s, r) => s + (r.confirmed_profit ?? 0), 0);
          const subBuyAmt = items.reduce((s, r) => s + (r.buy_amount       ?? 0), 0);
          const subSellAmt= items.reduce((s, r) => s + (r.sell_amount      ?? 0), 0);
          return (
            <React.Fragment key={acct}>
              {items.map((r, i) => {
                const cp = r.confirmed_profit ?? 0;
                const pr = r.profit_rate ?? 0;
                return (
                  <tr key={i}>
                    <td>{r.account_name}</td>
                    <td>{r.stock_name}</td>
                    <td className="ctr">{r.sell_date?.slice(0, 10) || '-'}</td>
                    <td className="num">{fNum(r.buy_price)}</td>
                    <td className="num">{fNum(r.sell_price)}</td>
                    <td className="num">{Number(r.quantity).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}</td>
                    <td className="num">{fNum(r.buy_amount)}</td>
                    <td className="num">{fNum(r.sell_amount)}</td>
                    <td className={`num ${colorCls(cp)}`}>{fSign(cp)}{fNum(cp)}</td>
                    <td className={`num ${colorCls(pr)}`}>{fSign(pr)}{Number(pr).toFixed(2)}%</td>
                  </tr>
                );
              })}
              <tr className="sp-subtotal-row">
                <td></td>
                <td>{acct} 소계</td>
                <td></td><td></td><td></td><td></td>
                <td className="num">{fNum(subBuyAmt)}</td>
                <td className="num">{fNum(subSellAmt)}</td>
                <td className={`num ${colorCls(subProfit)}`}>{fSign(subProfit)}{fNum(subProfit)}</td>
                <td></td>
              </tr>
            </React.Fragment>
          );
        })}
        <tr className="sp-total-row">
          <td></td>
          <td>전체 합계</td>
          <td></td><td></td><td></td><td></td>
          <td className="num">{fNum(grandBuyAmt)}</td>
          <td className="num">{fNum(grandSellAmt)}</td>
          <td className={`num ${colorCls(grandProfit)}`}>{fSign(grandProfit)}{fNum(grandProfit)}</td>
          <td></td>
        </tr>
      </tbody>
    </table>
  );
}


// ── 탭: 분배금/이자 ─────────────────────────────────────────

function PeriodDivTab({ data }) {
  if (!data) return <div className="sp-empty">데이터가 없습니다.</div>;
  if (!data.length) return <div className="sp-empty">해당 기간의 분배금 내역이 없습니다.</div>;
  const total = data.reduce((s, r) => s + (r.div_amount ?? 0), 0);
  return (
    <>
      <table className="sp-tbl">
        <thead>
          <tr>
            <th>계좌</th>
            <th>종목명</th>
            <th className="ctr">년월</th>
            <th className="num">분배금</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={i}>
              <td>{r.account_name}</td>
              <td>{r.stock_name}</td>
              <td className="ctr">{r.ym}</td>
              <td className="num pos">{fNum(r.div_amount)}원</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sp-summary-row">
        <span>합계: <strong className="pos">{fNum(total)}원</strong></span>
      </div>
    </>
  );
}
